import { QuestionWithId, QuestionUpdates } from '../types';
import { QuestionType } from 'services/campaign/types';
import { QuestionExpectedAnswer } from 'generated-graphql/types';

type Params = {
  questionsWithId: QuestionWithId[];
  setQuestions: (questions: QuestionWithId[]) => void;
};

export const useQuestionsHandler = ({
  questionsWithId: questions,
  setQuestions
}: Params) => {
  const addQuestion = (
    questionType: 'knockout' | 'qualifying' | 'document'
  ) => {
    // Map the question type to the appropriate properties
    const isDocument = questionType === 'document';
    const isMandatory =
      questionType === 'knockout' || questionType === 'document';
    const type = isDocument ? QuestionType.DOCUMENT : QuestionType.TEXT;
    const expectedAnswer =
      questionType === 'knockout' ? QuestionExpectedAnswer.IsYes : undefined;

    const questionsAfterAddition: QuestionWithId[] = [
      ...questions,
      {
        content: '',
        isMandatory,
        id: `question-${Math.random().toString(36).substring(7)}`,
        questionType: type,
        hasPreferredAnswer: false,
        preferredAnswerRequirements: [],
        documentTypes: isDocument ? [] : undefined,
        expectedAnswer
      }
    ];

    setQuestions(questionsAfterAddition);
  };

  const removeQuestion = (id: string) => {
    const questionsAfterRemoval = questions.filter((q) => q.id !== id);
    setQuestions(questionsAfterRemoval);
  };

  const handleQuestionsChange = (e: React.BaseSyntheticEvent, id: string) => {
    const value = e.target.value;
    const index = questions.findIndex((q) => q.id === id);
    const questionsToSet = [...questions];

    questionsToSet[index] = {
      ...questions[index],
      content: value
    };

    setQuestions(questionsToSet);
  };

  const setQuestionValue = (id: string, value: string) => {
    const index = questions.findIndex((q) => q.id === id);
    const questionsToSet = [...questions];

    questionsToSet[index] = {
      ...questions[index],
      content: value
    };

    setQuestions(questionsToSet);
  };

  const addPreferredAnswerValue = (id: string, value: string) => {
    const index = questions.findIndex((q) => q.id === id);
    const questionsToSet = [...questions];

    questionsToSet[index] = {
      ...questions[index],
      preferredAnswerText: value,
      hasPreferredAnswer: !!value
    };

    setQuestions(questionsToSet);
  };

  const setExpectedAnswer = (id: string, value: QuestionExpectedAnswer) => {
    const index = questions.findIndex((q) => q.id === id);
    const questionsToSet = [...questions];

    questionsToSet[index] = {
      ...questions[index],
      expectedAnswer: value
    };

    setQuestions(questionsToSet);
  };

  // Document question handlers
  const setDocumentTypes = (questionId: string, documentTypes: string[]) => {
    const index = questions.findIndex((q) => q.id === questionId);
    if (index === -1) return;

    const questionsToSet = [...questions];
    const question = questions[index];

    if (question.questionType !== QuestionType.DOCUMENT) {
      return;
    }

    questionsToSet[index] = {
      ...questions[index],
      documentTypes
    };

    setQuestions(questionsToSet);
  };

  const moveQuestion = ({
    id,
    direction
  }: {
    id: string;
    direction: 'up' | 'down';
  }) => {
    const index = questions.findIndex((q) => q.id === id);

    if (direction === 'up') {
      if (index <= 0) return; // Can't move up if it's the first item

      const questionsToSet = [...questions];
      const temp = questionsToSet[index];
      questionsToSet[index] = questionsToSet[index - 1];
      questionsToSet[index - 1] = temp;

      setQuestions(questionsToSet);
    } else if (direction === 'down') {
      if (index === -1 || index >= questions.length - 1) return; // Can't move down if it's the last item

      const questionsToSet = [...questions];
      const temp = questionsToSet[index];
      questionsToSet[index] = questionsToSet[index + 1];
      questionsToSet[index + 1] = temp;

      setQuestions(questionsToSet);
    }
  };

  // Generic function to update multiple properties of a question at once
  const updateQuestion = (id: string, updates: QuestionUpdates) => {
    if (!questions || questions.length === 0) return;

    const index = questions.findIndex((q) => q.id === id);
    if (index === -1) return;

    const questionsToSet = [...questions];
    questionsToSet[index] = {
      ...questions[index],
      ...updates
    };

    setQuestions(questionsToSet);
  };

  return {
    addQuestion,
    removeQuestion,
    handleQuestionsChange,
    setQuestionValue,
    addPreferredAnswerValue,
    setExpectedAnswer,
    moveQuestion,
    setDocumentTypes,
    updateQuestion
  };
};
